package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.browser.window
import org.example.project.dashboard.ScreenManager
import org.example.project.dashboard.AppDialogs
import org.example.project.dashboard.TrackedScreenHost
import org.example.project.engineslot.SlotActionBarRow
import org.example.project.engineslot.SlotStyles
import org.example.project.service.LoginService
import org.example.project.service.GlobalManager
import org.example.project.service.RollenService
import org.example.project.settings.*
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/** Internal sales dashboard; customer navigation uses VertriebBereichWrapper. */
@Composable
fun SysVertriebsDashboard(node: String) {
    if (node != "SYS" || !ScreenManager.isLinkedInNode()) return
    val scope = rememberCoroutineScope()
    var syncNow by remember { mutableStateOf(kotlin.js.Date().getTime()) }
    LaunchedEffect(Unit) {
        while (true) { delay(30_000); syncNow = kotlin.js.Date().getTime() }
    }
    LaunchedEffect(LoginService.getId()) {
        try { LinkedInSyncState.status = LinkedInFlowApi.syncStatus() }
        catch (e: CancellationException) { throw e }
        catch (e: Exception) { AppDialogs.alert(e.message ?: "LinkedIn-Snapshot konnte nicht geprüft werden.") }
    }
    val initialContact = remember {
        window.location.hash.substringAfter('?', "").split('&').firstOrNull { it.substringBefore('=') == "linkedinContact" }
            ?.substringAfter('=')?.let { js("decodeURIComponent")(it) as String }?.takeIf { it.isNotBlank() }
    }
    val initialCall = remember {
        window.location.hash.substringAfter('?', "").split('&').firstOrNull { it.substringBefore('=') == "callProspect" }
            ?.substringAfter('=')?.let { js("decodeURIComponent")(it) as String }?.takeIf { it.isNotBlank() }
    }
    var flowContactId by remember { mutableStateOf(initialContact ?: initialCall?.let { "web-$it" }) }
    val initialCheckpoint = remember {
        window.location.hash.substringAfter('?', "").split('&').firstOrNull { it.substringBefore('=') == "linkedinCheckpoint" }
            ?.substringAfter('=')?.let { js("decodeURIComponent")(it) as String }?.takeIf { value -> isCheckpointView(value) }
    }
    var selectedLinkedInKey by remember { mutableStateOf(when {
        window.location.hash.contains("salesSettings=profiles") -> "LinkedInFlow"
        initialCall != null || initialContact != null || initialCheckpoint != null -> "LinkedInFlow"
        else -> ""
    }) }
    val all = ScreenManager.childScreensForKey("SYSVertriebsDashboard", node)
        .filter { RollenService.hasReadPermission(it.key) }
    val keys = listOf("Vertriebsindex", "Konkurrenzindex", "Vertriebspool", "MeineLeads", "LinkedInChat", "LinkedInFlow", "Leaduebersicht")
    val screens = keys.mapNotNull { key -> all.firstOrNull { it.key == key } }
    val selectedKey = selectedLinkedInKey
    val active = screens.firstOrNull { it.key == selectedKey } ?: screens.firstOrNull()
    val kpis = remember { LinkedInKpiState(scope) }
    LaunchedEffect(LinkedInSyncState.version) {
        while (true) {
            try { kpis.refresh() }
            catch (e: CancellationException) { throw e }
            catch (_: Exception) { /* Keep the last snapshot visible while the API is unavailable. */ }
            delay(180_000)
        }
    }
    Style(SlotStyles)
    CompositionLocalProvider(LocalLinkedInKpis provides kpis) {
    Div({ classes(SlotStyles.screenRoot) }) {
        SlotActionBarRow({}, {
            Div({ style { width(100.percent); minWidth(0.px); overflowX("auto") } }) {
              Div({ style { display(DisplayStyle.Flex); property("width", "max-content"); minWidth(100.percent); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); flexWrap(FlexWrap.Nowrap); gap(12.px) } }) {
                Div({ style { display(DisplayStyle.Flex); flexShrink(0); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(12.px); flexWrap(FlexWrap.Nowrap) } }) {
                    screens.forEach { screen ->
                        LumaticaButton(if (screen.key == "LinkedInFlow") "Flow" else screen.label,
                            { selectedLinkedInKey = screen.key; kpis.selected = "" },
                            variant = LumaticaButtonVariant.State, isActive = active?.key == screen.key)
                    }
                    run {
                        val sync = LinkedInSyncState.status
                        val pause = kotlin.js.Date(sync.linkedinSyncPauseBis).getTime()
                        val paused = !pause.isNaN() && pause > syncNow
                        val canSync = !LinkedInSyncState.busy && RollenService.hasActionPermission("LinkedInFlow", RollenService.ACTION_EDIT)
                        Div({
                            attr("title", "Kontakte: ${sync.linkedinKontakteSyncAm.ifBlank { "noch nicht synchronisiert" }} · Chats: ${sync.linkedinChatSyncAm.ifBlank { "noch nicht synchronisiert" }}" +
                                if (sync.linkedinSyncWarnung.isNotBlank()) " · ${sync.linkedinSyncWarnung}" else "")
                            style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) }
                        }) {
                            Button(attrs = {
                                attr("aria-label", "LinkedIn synchronisieren")
                                attr("title", "LinkedIn synchronisieren")
                                if (!canSync) attr("disabled", "")
                                style {
                                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                                    width(28.px); height(36.px); padding(0.px); border(0.px); backgroundColor(Color.transparent)
                                    cursor(if (canSync) "pointer" else "default"); if (!canSync) opacity(.45)
                                }
                                onClick {
                                scope.launch {
                                    try { LinkedInFlowApi.synchronize() }
                                    catch (e: CancellationException) { throw e }
                                    catch (e: Exception) {
                                        runCatching { LinkedInSyncState.status = LinkedInFlowApi.syncStatus() }
                                        AppDialogs.alert(e.message ?: "LinkedIn-Sync fehlgeschlagen. Der Snapshot bleibt erhalten.")
                                    }
                                }
                                }
                            }) {
                                Img(src = "images/refresh.svg", attrs = { attr("alt", ""); style { width(20.px); height(20.px); property("filter", "brightness(0) invert(1)") } })
                            }
                        Span({ attr("role", "status"); attr("aria-live", "polite"); style { fontSize(12.px); whiteSpace("nowrap") } }) {
                            Text("${sync.linkedinSnapshotKontakteAnzahl} Kontakte · ${sync.linkedinDatensaetzeAnzahl} Datensätze · " + when {
                                LinkedInSyncState.busy -> "Synchronisiert …"
                                paused -> "LinkedIn-Pause bis ${kotlin.js.Date(sync.linkedinSyncPauseBis).toLocaleString()}"
                                sync.linkedinSyncWarnung.isNotBlank() -> "LinkedIn-Abruf nicht vollständig"
                                sync.linkedinChatSyncAm.isNotBlank() -> "Stand: ${kotlin.js.Date(sync.linkedinChatSyncAm).toLocaleString()}"
                                else -> "Noch nicht synchronisiert"
                            })
                        }
                        }
                    }
                }
              }
            }
        }, {}, fullWidthCenter = true)
        Div({ style { width(100.percent); height(1.px); backgroundColor(Color("rgba(255,255,255,.14)")); flexShrink(0) } })
        run {
            LinkedInCheckpointTiles(kpis.contacts, kpis.selected,
                if (active?.key == "LinkedInFlow") kpis.enabled else
                    active?.key == "Leaduebersicht" || RollenService.hasReadPermission("LinkedInFlow"),
                kpis.deadlines, onSelect = { target ->
                    if (active?.key == "Leaduebersicht" && target != "MY_LEADS") {
                        kpis.select(if (kpis.selected == target) "" else target)
                    } else {
                        kpis.select(target)
                        if (active?.key != "LinkedInFlow") { flowContactId = null; selectedLinkedInKey = "LinkedInFlow" }
                    }
                })
            Div({ style { width(100.percent); height(1.px); backgroundColor(Color("rgba(255,255,255,.14)")); flexShrink(0) } })
        }
        Div({ style { flex("1"); minHeight(0.px); overflow("hidden") } }) {
            if (active == null) Text("Keine freigegebenen Bereiche vorhanden.")
            if (active != null) key(active.key) {
                TrackedScreenHost(active) {
                    if (active.key == "LinkedInChat") LinkedInChat(node) { id -> flowContactId = id; selectedLinkedInKey = "LinkedInFlow" }
                    else if (active.key == "LinkedInFlow") LinkedInLeadUebernahme(node, initialContactId = flowContactId,
                        initialCheckpoint = kpis.selected.ifBlank { initialCheckpoint ?: "MY_LEADS" })
                    else if (active.key == "Leaduebersicht") Leaduebersicht()
                    else active.content?.invoke(active)
                }
            }
        }
    }
    }
}
